import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_NAME, getSiteUrl, SITEMAP_BASE_URL } from "../../lib/siteConfig";
import { buildOrganizationAndWebsiteGraph } from "../../lib/seoJsonLd";

/**
 * @param {object} props
 * @param {string} props.title - full page title (include brand if desired)
 * @param {string} props.description
 * @param {string} props.path - pathname incl. leading slash
 * @param {string} [props.ogImage]
 * @param {'website'|'article'|'product'} [props.ogType]
 * @param {object[]} [props.jsonLd] - extra JSON-LD objects (merged into @graph when single script used)
 * @param {boolean} [props.includeDefaultGraph]
 */
export default function SEO({
  title,
  description,
  path,
  ogImage,
  ogType = "website",
  jsonLd = [],
  includeDefaultGraph = true,
}) {
  const base = getSiteUrl();
  const canonical = base ? `${base}${path.startsWith("/") ? path : `/${path}`}` : path;
  const defaultOgImage = `${SITEMAP_BASE_URL}/images/moss-matrix-logo-circle.png`;
  const image = ogImage || defaultOgImage;

  const defaultGraph = includeDefaultGraph ? buildOrganizationAndWebsiteGraph(path) : null;
  const scripts = [];

  if (defaultGraph && jsonLd.length === 0) {
    scripts.push(defaultGraph);
  } else if (defaultGraph && jsonLd.length > 0) {
    const extra = jsonLd.filter(Boolean);
    const merged = {
      "@context": "https://schema.org",
      "@graph": [...(defaultGraph["@graph"] || []), ...extra],
    };
    scripts.push(merged);
  } else if (jsonLd.length > 0) {
    const graphOnly = jsonLd.length === 1 && jsonLd[0]["@graph"] ? jsonLd[0] : { "@context": "https://schema.org", "@graph": jsonLd };
    scripts.push(graphOnly);
  }

  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-GB" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {base ? <link rel="canonical" href={canonical} /> : null}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {base ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {scripts.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
}
